import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Split-screen auth layout (form left, hero image right).
 * Mirrors the Workwise "Einloggen als Unternehmen" pattern:
 *   - Pink #EC4899 primary
 *   - Light lavender hero panel with grayscale photo and pink decorative half-circle
 */
const AuthLayout = ({
  children,
  heroImage = 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1400&q=80',
  heroAlt = 'Modern hiring',
  supportEmail = 'support@jobasti.com',
  supportPhone = '+49 30 123 456 78',
}) => {
  const { t } = useTranslation();
  // Remember which landing the user came from so the auth-screen logo
  // takes them back to *that* landing (companies vs jobseeker).
  let logoTarget = '/';
  try {
    if (typeof window !== 'undefined' && sessionStorage.getItem('jobasti_landing_source') === 'jobseeker') {
      logoTarget = '/for-jobseekers';
    }
  } catch { /* noop */ }
  return (
    <div
      className="min-h-screen flex bg-white"
      style={{ fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif" }}
      data-testid="auth-layout"
    >
      {/* LEFT — form panel */}
      <div className="flex-1 flex flex-col">
        {/* Top nav row (matches in-app Navigation: h-16, px-4/6/8, text-2xl logo) */}
        <div className="h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b border-gray-100">
          <Link to={logoTarget} data-testid="auth-logo" className="inline-flex items-center">
            <img src="/brand-flame.png" alt="JobastiIQ" className="h-10 w-auto" />
          </Link>
          <LanguageSwitcher />
        </div>

        {/* Form content */}
        <div className="flex-1 flex flex-col justify-center w-full max-w-md mx-auto px-6 py-10">
          {children}
        </div>

        {/* Footer: support */}
        <div className="px-6 pb-10 text-xs text-gray-500 max-w-md mx-auto w-full">
          <p className="font-semibold text-gray-700 mb-1.5">{t('authLayout.needHelp')}</p>
          <p>
            <a href={`mailto:${supportEmail}`} className="text-blue-500 hover:text-blue-600">{supportEmail}</a>
            <span className="mx-2 text-gray-300">·</span>
            <span>{supportPhone}</span>
          </p>
        </div>
      </div>

      {/* RIGHT — hero panel (hidden on mobile, full-cover colour image) */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src={heroImage}
          alt={heroAlt}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export default AuthLayout;
